import { Box, useTheme } from '@mui/material';
import React from 'react';
import SectionHeader from '../components/atomic/SectionHeader';
import Header from '../components/Header';
import { FooterNew } from '../components/organisms';

/**
 * Flexible layout component for rich content pages
 *
 * This component provides a standardized layout for content-rich pages that need
 * more flexibility than LegalPageLayout but don't fit the FormPageLayout pattern.
 *
 * FEATURES:
 * - Standard header and footer
 * - Optional SectionHeader with green bars (can be toggled on/off)
 * - Flexible background color customization
 * - Customizable container max-width
 * - Support for complex layouts (cards, grids, sections, CTAs)
 * - Uses theme tokens for consistent theming
 *
 * WHEN TO USE:
 * - Content pages with rich layouts (JoinConsultant, JoinIntegrator, CollaborateWithUs)
 * - Pages that need optional SectionHeader pattern
 * - Pages with custom backgrounds or container widths
 * - Pages that don't fit FormPageLayout or LegalPageLayout patterns
 *
 * WHEN NOT TO USE:
 * - For form/CTA pages (use FormPageLayout)
 * - For simple legal/informational pages (use LegalPageLayout)
 * - For home page (intentionally unique structure)
 * - For UpdatesPage (allowed exception)
 *
 * EXAMPLE USAGE - With Header:
 * ```jsx
 * <ContentPageLayout
 *   useHeader={true}
 *   title="Join Our Professional Services"
 *   subtitle="Partner with The Collective"
 *   headerDark={false}
 *   backgroundColor="#f8f9fa"
 *   maxWidth="lg"
 * >
 *   <Container maxWidth="lg" sx={{ pb: 8 }}>
 *     {/* Your page content here *\/}
 *   </Container>
 * </ContentPageLayout>
 * ```
 *
 * EXAMPLE USAGE - Without Header (Custom Header):
 * ```jsx
 * <ContentPageLayout backgroundColor="#f8f9fa">
 *   <Section sx={{ py: 0 }}>
 *     {/* Custom header implementation *\/}
 *   </Section>
 *   <Container maxWidth="lg">
 *     {/* Your page content here *\/}
 *   </Container>
 * </ContentPageLayout>
 * ```
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The page content to render
 * @param {boolean} [props.useHeader] - Whether to show SectionHeader with green bars
 * @param {string} [props.title] - SectionHeader title (required if useHeader=true)
 * @param {string} [props.subtitle] - SectionHeader subtitle (optional if useHeader=true)
 * @param {boolean} [props.headerDark] - Use dark theme for SectionHeader
 * @param {string} [props.backgroundColor] - Background color for main content area (uses theme.palette.background.section by default)
 * @param {object} [props.containerSx] - Additional sx props for container
 * @returns {React.ReactElement} The content page layout
 */
function ContentPageLayout({
  children,
  useHeader = false,
  title = '',
  subtitle = '',
  headerDark = false,
  backgroundColor,
  containerSx = {},
}) {
  const theme = useTheme();

  // Use theme token for default background color
  const bgColor = backgroundColor || theme.palette.background.section;

  return (
    <Box
      sx={{
        textAlign: 'left',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header
        scrollToCollective={() => {}}
        scrollToServices={() => {}}
        scrollToContact={() => {}}
      />
      <Box
        component="main"
        sx={{
          flex: 1,
          backgroundColor: bgColor,
          ...containerSx,
        }}
      >
        {useHeader && (
          <Box sx={{ pt: { xs: 8, md: 10 } }}>
            <SectionHeader
              title={title}
              subtitle={subtitle}
              dark={headerDark}
            />
          </Box>
        )}
        {children}
      </Box>
      <FooterNew />
    </Box>
  );
}

export default ContentPageLayout;
